import {
  adjustContentVisibility,
  Section,
  SectionParagraph as Subtitle,
  SectionTitle as Title,
} from '@/components/shared/Content';
import type { CarMode } from './types';
import { useElementLerp } from '@/hooks/useElementLerp';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { Box, Flex, font, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import React, { memo, useContext, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import styled from 'styled-components';
import { fastHelpImg, fastHelpImgFsd, thereWhenNeededImg, thereWhenNeededImgFsd } from './assets/claims';
import { CarPageContext } from './CarPageContext';

const ThereWhenNeeded = styled(Image)`
  position: absolute;
  left: 16px;
  transform: translateX(-100%);
  top: -17px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const ThereWhenNeededFsd = styled(Image)`
  position: absolute;
  left: -20px;
  transform: translateX(-100%);
  top: 6px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const FastHelp = styled(Image)`
  position: absolute;
  left: 100%;
  transform: translateX(-30px);
  top: -40px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const FastHelpFsd = styled(Image)`
  position: absolute;
  left: calc(100% + 20px);
  top: 6px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const Explanation = styled(Box)`
  display: block;
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '1.25',
  })}
  ${MEDIA_QUERIES.tablet`
    ${font('main', {
      fontSize: '2rem',
      lineHeight: '1.25',
    })}
  `}
  ${MEDIA_QUERIES.desktop`
    display: none
  `}
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

interface ClaimsProps {
  t?: number;
  title?: string;
  subtitle?: string;
  mode?: CarMode;
}

interface TitleSectionProps {
  title: string;
  subtitle: string;
}

const TitleSection = memo(
  ({ title, subtitle }: TitleSectionProps) => (
    <Flex gap="20px" flexDirection="column" width="100%">
      <Title tabIndex={0}>{parse(title)}</Title>
      <Box mx="auto" width="90%">
        <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
      </Box>
    </Flex>
  ),
  (prevProps, nextProps) => prevProps.title === nextProps.title && prevProps.subtitle === nextProps.subtitle,
);

TitleSection.displayName = 'TitleSection';

const Claims: React.FC<ClaimsProps> = ({
  t,
  title = '21st-century, award-winning claims service',
  subtitle = "Our claims process is fast, seamless, and hassle-free. We'll have your back whether you file a claim after\na crash, if your car's been vandalized, or if you need a tow in the middle of nowhere.",
  mode = 'default',
}) => {
  const { analytics } = useContext(CarPageContext);
  const { absoluteScroll } = useSmoothScroll();
  const [ref, newT] = useElementLerp(t ? t : absoluteScroll, {
    startPoint: mode === 'tesla' ? 0.42 : 0.3,
  });
  const leftPhoneLottieRef = useRef<HTMLDivElement>(null);
  const rightPhoneLottieRef = useRef<HTMLDivElement>(null);

  useLottie(leftPhoneLottieRef, {
    animationData:
      mode === 'tesla'
        ? import('./assets/claims/left_phone-lottie-fsd.json')
        : import('./assets/claims/left_phone-lottie.json'),
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'contain',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  useLottie(rightPhoneLottieRef, {
    animationData:
      mode === 'tesla'
        ? import('./assets/claims/right_phone-lottie-fsd.json')
        : import('./assets/claims/right_phone-lottie.json'),
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'contain',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'claims',
    page: analytics?.page,
    position: 9,
  });

  return (
    <StyledSection
      mb={0}
      pt={[45, 100]}
      pb={150}
      width={1}
      ref={mergeRefs([ref, intersectionRef])}
      aria-label="Lemonade car insurance app, faster everything"
      contain="paint"
    >
      <TitleSection title={title} subtitle={subtitle} />
      <Flex mt={40}>
        <Box
          height={mode === 'tesla' ? [370, 380, 380] : [290, 500]}
          mr={mode === 'tesla' ? [-10, 12, 12] : [-15, -30, 10]}
          style={{ zIndex: 1, transform: `translateY(${newT * 50}px)` }}
          ref={leftPhoneLottieRef}
        >
          {mode === 'tesla' ? (
            <ThereWhenNeededFsd
              src={thereWhenNeededImgFsd.src}
              alt="There when you need it"
              width={thereWhenNeededImgFsd.width / 2}
              height={thereWhenNeededImgFsd.height / 2}
            />
          ) : (
            <ThereWhenNeeded src={thereWhenNeededImg.src} alt="There when you need it" width={129} height={111} />
          )}
          <Explanation mr={mode === 'tesla' ? [0, 0, 0] : [10, 40]} mb={mode === 'tesla' ? 20 : 0}>
            <b>There when</b>
            <br />
            <span style={{ fontWeight: mode === 'tesla' ? 700 : 400 }}>you need us</span>
          </Explanation>
        </Box>
        <Box
          height={mode === 'tesla' ? [370, 380, 380] : [290, 500]}
          ml={mode === 'tesla' ? [-10, 12, 12] : [-15, -30, 10]}
          style={{ transform: `translateY(${(1 - newT) * 50}px)` }}
          ref={rightPhoneLottieRef}
        >
          {mode === 'tesla' ? (
            <FastHelpFsd
              src={fastHelpImgFsd.src}
              alt="Fast help and claims resolution"
              width={fastHelpImgFsd.width / 2}
              height={fastHelpImgFsd.height / 2}
            />
          ) : (
            <FastHelp src={fastHelpImg.src} alt="Fast help and claims resolution" width={165} height={111} />
          )}
          <Explanation ml={mode === 'tesla' ? [0, 0, 0] : [10, 0]} mb={mode === 'tesla' ? 20 : 0}>
            <b>Super fast</b>
            <br />
            <span style={{ fontWeight: mode === 'tesla' ? 700 : 400 }}>claim handling</span>
          </Explanation>
        </Box>
      </Flex>
    </StyledSection>
  );
};

export default React.memo(Claims);
